import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

export const isReadCancelled = error => error?.code === 'ERR_CANCELED';
const cancelled = () => Object.assign(new Error('Read cancelled'), { code: 'ERR_CANCELED' });
const transient = error => ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error?.code)
  || [502, 503, 504].includes(error?.response?.status);

// Read-only recovery: never replay a payment, upload, POST or other mutation.
export function createReadRecovery(transport = axios.get, report = () => {}) {
  const pending = new Map();
  let disposed = false;
  const notify = () => report([...pending.values()].map(item => item.status).filter(Boolean));
  const get = async (url, options = {}) => {
    if (disposed) throw cancelled();
    if (options.method && options.method.toUpperCase() !== 'GET') throw new Error('GET only');
    const { recoveryOptional, ...requestOptions } = options;
    const key = `${url}:${JSON.stringify(options.params || {})}`;
    for (const [id, old] of pending) {
      if (old.key === key) { old.controller.abort(); pending.delete(id); }
    }
    const controller = new AbortController();
    const item = { controller, key, status: null };
    pending.set(controller, item);
    let attempt = 0;
    try {
      while (!controller.signal.aborted) {
        try {
          const result = await transport(url, { ...requestOptions, method: 'GET', timeout: 10000, signal: controller.signal });
          if (controller.signal.aborted || disposed) throw cancelled();
          pending.delete(controller); notify();
          return result;
        } catch (error) {
          if (controller.signal.aborted || disposed || isReadCancelled(error)) throw cancelled();
          if (!transient(error)) {
            if (recoveryOptional) pending.delete(controller); else item.status = 'failed';
            notify();
            throw error;
          }
          item.status = 'reconnecting'; notify();
          const delay = Math.min(2000 * (2 ** Math.min(attempt++, 3)), 15000);
          await new Promise((resolve, reject) => {
            const abort = () => { clearTimeout(timer); reject(cancelled()); };
            const timer = setTimeout(() => { controller.signal.removeEventListener('abort', abort); resolve(); }, delay);
            controller.signal.addEventListener('abort', abort, { once: true });
          });
        }
      }
      throw cancelled();
    } finally {
      if (controller.signal.aborted || disposed) { pending.delete(controller); notify(); }
    }
  };
  return {
    get,
    activate() { disposed = false; },
    dispose() {
      disposed = true;
      for (const item of pending.values()) item.controller.abort();
      pending.clear(); notify();
    },
  };
}

export function useRecoverableRead(scope) {
  const [state, setState] = useState({ owner: null, statuses: [] });
  const recovery = useMemo(() => {
    const instance = createReadRecovery(undefined, statuses => setState({ owner: instance, statuses }));
    return instance;
  }, [scope]);
  useEffect(() => { recovery.activate(); return () => recovery.dispose(); }, [recovery]);
  return { get: recovery.get, statuses: state.owner === recovery ? state.statuses : [] };
}
